import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { PermissionsService } from 'src/app/auth/permissions.service';
import { SalesService } from 'src/app/mods/sales/sales.service';
import { SpecialOrderNotificationsService, SpecialOrderNotificationEvent, SpecialOrderNotificationThread } from 'src/app/shared/collaboration/special-order-notifications.service';
import { OverlayUsersService } from 'src/app/shared/collaboration/overlay-users.service';
import { DocumentsApiService } from 'src/app/shared/documents/services/documents-api.service';
import { formatLocalDateTime as formatLocalDateTimeValue } from 'src/app/shared/constants/date-utils';

interface SpecialOrderPersonDisplay {
  name: string;
  ev: string | null;
}

interface SpecialOrderThreadDisplay {
  customerName: string;
  sellers: SpecialOrderPersonDisplay[];
  assigned: SpecialOrderPersonDisplay | null;
  processStatus: string;
}

@Component({
  standalone: false,
  selector: 'app-home-special-order-notifications-dialog',
  templateUrl: './home-special-order-notifications-dialog.component.html',
  styleUrls: ['./home-special-order-notifications-dialog.component.scss'],
})
export class HomeSpecialOrderNotificationsDialogComponent implements OnInit {
  private readonly service = inject(SpecialOrderNotificationsService);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly router = inject(Router);
  private readonly permissions = inject(PermissionsService);
  private readonly destroyRef = inject(DestroyRef);
  private eventsSubscription?: Subscription;
  private displaysSubscription?: Subscription;

  readonly threads = signal<SpecialOrderNotificationThread[]>([]);
  readonly events = signal<SpecialOrderNotificationEvent[]>([]);
  readonly selectedThread = signal<SpecialOrderNotificationThread | null>(null);
  readonly loading = signal(true);
  readonly eventsLoading = signal(false);
  readonly errorMessage = signal('');
  readonly threadDisplays = signal<Record<number, SpecialOrderThreadDisplay>>({});

  readonly canOpenTracking = computed(() => this.permissions.can('invoices:view'));

  private readonly documentsApi = inject(DocumentsApiService);
  private readonly sales = inject(SalesService);
  private readonly overlayUsers = inject(OverlayUsersService);

  ngOnInit(): void {
    this.service.threads$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((threads) => {
        this.threads.set(threads);
        if (!threads.some((thread) => thread.id === this.selectedThread()?.id)) {
          this.eventsSubscription?.unsubscribe();
          this.selectedThread.set(null);
          this.events.set([]);
          this.eventsLoading.set(false);
        }
        if (!threads.length) this.threadDisplays.set({});
        this.loadThreadDisplays(threads);
      });
    this.loadThreads();
  }

  loadThreads(): void {
    this.loading.set(true);
    this.errorMessage.set('');
    this.service.listThreads().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.loading.set(false); },
      error: () => {
        this.loading.set(false);
        this.errorMessage.set('No fue posible cargar los avisos de Compras.');
      },
    });
  }

  selectThread(thread: SpecialOrderNotificationThread): void {
    this.eventsSubscription?.unsubscribe();
    this.selectedThread.set(thread);
    this.events.set([]);
    this.errorMessage.set('');
    this.eventsLoading.set(true);
    this.eventsSubscription = this.service.getThreadEvents(thread.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.events.set(response?.items || []);
        this.eventsLoading.set(false);
        const latestEvent = this.events()[0];
        if (latestEvent?.id && thread.unreadCount > 0) {
          this.service.markThreadRead(thread.id, latestEvent.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            error: () => undefined,
          });
        }
      },
      error: () => {
        this.eventsLoading.set(false);
        this.errorMessage.set('No fue posible cargar el historial del aviso.');
      },
    });
  }

  openTracking(thread: SpecialOrderNotificationThread): void {
    if (thread.scopeType === 'receipt_unconsidered') {
      const receiptId = Number(thread.scopeId || 0);
      void this.router.navigate(['/compras-v2/recepciones'], {
        queryParams: receiptId ? { receiptId } : undefined,
      }).then(() => this.dialogRef.close());
      return;
    }
    if (!this.canOpenTracking()) return;
    const queryParams: Record<string, number> = {};
    if (thread.originDocumentId) queryParams.documentId = thread.originDocumentId;
    if (thread.originDocumentItemId) queryParams.documentItemId = thread.originDocumentItemId;
    void this.router.navigate(['/ventas/invoices'], { queryParams }).then(() => this.dialogRef.close());
  }

  threadTitle(thread: SpecialOrderNotificationThread): string {
    if (thread.scopeType === 'receipt_unconsidered') {
      const snapshot = this.threadSnapshot(thread);
      const sku = String(snapshot.sku || '').trim();
      const receipt = String(snapshot.receipt_number || thread.scopeId || '');
      return sku ? `Item no considerado ${sku}` : `Item no considerado · Recepción ${receipt}`;
    }
    return `Pedido especial #${thread.originDocumentItemId || thread.id}`;
  }

  threadSubtitle(thread: SpecialOrderNotificationThread): string {
    if (thread.scopeType === 'receipt_unconsidered') {
      const note = String(this.threadSnapshot(thread).note || '').trim();
      return note || 'Pendiente de empatar por Compras';
    }
    return `Fecha estimada: ${this.formatDate(thread.latestEvent?.afterDate)}`;
  }

  threadCustomerName(thread: SpecialOrderNotificationThread): string {
    return this.threadDisplays()[thread.id]?.customerName || 'Cliente no disponible';
  }

  threadSalesPeople(thread: SpecialOrderNotificationThread): string {
    const sellers = this.threadDisplays()[thread.id]?.sellers || [];
    return sellers.length ? sellers.map((seller) => this.personLabel(seller)).join(' / ') : 'No disponible';
  }

  threadAssignedPerson(thread: SpecialOrderNotificationThread): string {
    const assigned = this.threadDisplays()[thread.id]?.assigned;
    return assigned ? this.personLabel(assigned) : 'No disponible';
  }

  threadStatusLabel(thread: SpecialOrderNotificationThread): string {
    return this.threadDisplays()[thread.id]?.processStatus || 'Pendiente';
  }

  private threadSnapshot(thread: SpecialOrderNotificationThread): Record<string, any> {
    const snapshot = thread?.latestEvent?.snapshot;
    if (!snapshot || typeof snapshot !== 'object') return {};
    return snapshot as Record<string, any>;
  }

  isUnconsideredThread(thread: SpecialOrderNotificationThread): boolean {
    return thread.scopeType === 'receipt_unconsidered';
  }

  isUnconsideredEvent(event: SpecialOrderNotificationEvent): boolean {
    return event.scopeType === 'receipt_unconsidered'
      || event.notificationKind === 'unconsidered_item_sku';
  }

  eventSnapshot(event: SpecialOrderNotificationEvent): Record<string, any> {
    const snapshot = event?.snapshot;
    if (!snapshot || typeof snapshot !== 'object') return {};
    return snapshot as Record<string, any>;
  }

  eventSnapshotSku(event: SpecialOrderNotificationEvent): string {
    return String(this.eventSnapshot(event).sku || '').trim();
  }

  eventSnapshotReceipt(event: SpecialOrderNotificationEvent): string {
    return String(this.eventSnapshot(event).receipt_number || '').trim();
  }

  eventSnapshotNote(event: SpecialOrderNotificationEvent): string {
    return String(this.eventSnapshot(event).note || '').trim();
  }

  private loadThreadDisplays(threads: SpecialOrderNotificationThread[]): void {
    this.displaysSubscription?.unsubscribe();
    const displayCache = this.threadDisplays();
    const documentIds = [...new Set(
      threads
        .filter((thread) => !this.isUnconsideredThread(thread))
        .filter((thread) => !displayCache[thread.id])
        .map((thread) => Number(thread.originDocumentId || 0))
        .filter((documentId) => documentId > 0),
    )];
    if (!documentIds.length) return;

    this.displaysSubscription = forkJoin({
      assistants: this.sales.getAsistentes().pipe(catchError(() => of([]))),
      users: this.overlayUsers.getUsers().pipe(catchError(() => of([]))),
      documents: forkJoin(documentIds.map((documentId) => this.documentsApi.getDocument(documentId).pipe(
        map((document) => ({ documentId, document })),
        catchError(() => of({ documentId, document: null })),
      ))),
      trackers: forkJoin(documentIds.map((documentId) => this.documentsApi.getSpecialOrderTracker(documentId).pipe(
        map((response) => ({ documentId, tracker: response?.data })),
        catchError(() => of({ documentId, tracker: null })),
      ))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ assistants, users, documents, trackers }) => {
        const documentsById = new Map(documents.map((entry) => [entry.documentId, entry.document]));
        const trackersById = new Map(trackers.map((entry) => [entry.documentId, entry.tracker]));
        const displayPatch: Record<number, SpecialOrderThreadDisplay> = {};

        for (const thread of threads) {
          if (this.isUnconsideredThread(thread) || !documentsById.has(Number(thread.originDocumentId))) continue;
          displayPatch[thread.id] = this.buildThreadDisplay(
            thread,
            documentsById.get(Number(thread.originDocumentId)),
            trackersById.get(Number(thread.originDocumentId)),
            Array.isArray(assistants) ? assistants : [],
            Array.isArray(users) ? users : [],
          );
        }

        if (Object.keys(displayPatch).length) {
          this.threadDisplays.update((current) => ({ ...current, ...displayPatch }));
        }
      });
  }

  private buildThreadDisplay(
    thread: SpecialOrderNotificationThread,
    document: any,
    tracker: any,
    assistants: any[],
    users: any[],
  ): SpecialOrderThreadDisplay {
    const header = document?.document_header || {};
    const customer = header.cliente || {};
    const customerName = this.firstText(
      customer.nombre && customer.apellido ? `${customer.nombre} ${customer.apellido}` : customer.nombre,
      header.customer_name && header.customer_apellido ? `${header.customer_name} ${header.customer_apellido}` : header.customer_name,
      header.clienteControl?.name,
    );

    const item = this.documentItemForThread(document?.items, thread.originDocumentItemId);
    const itemRow = item?.line_rows?.find((row: any) => Number(row?.id || 0) === Number(thread.originDocumentItemId || 0)) || item || {};
    const sellerCandidates = [
      { id: itemRow.id_user_added ?? item?.id_user_added, name: itemRow.user_name || itemRow.name_user_added || item?.user_name || item?.name_user_added },
      { id: itemRow.id_user_shared_sale ?? item?.id_user_shared_sale, name: itemRow.name_user_shared || item?.name_user_shared },
    ].filter((candidate) => Number(candidate.id || 0) > 0 || String(candidate.name || '').trim());

    const sellers = sellerCandidates
      .map((candidate) => this.resolvePerson(candidate.id, candidate.name, assistants, users))
      .filter((person): person is SpecialOrderPersonDisplay => !!person)
      .filter((person, index, list) => list.findIndex((other) =>
        other.name === person.name && other.ev === person.ev,
      ) === index);

    const assignedAssistant = customer.ejecutivo || header.selectedCliente?.asistente || {};
    const assignedAssistantRecord = assistants.find((assistant) =>
      Number(assistant?.idAsistans || 0) === Number(assignedAssistant?.idAsistans || assignedAssistant?.id || 0),
    );
    const assignedUserId = Number(assignedAssistantRecord?.Usuario || assignedAssistant?.Usuario || 0);
    const assignedUser = users.find((user) => Number(user?.id || 0) === assignedUserId);
    const assigned = this.resolvePerson(
      assignedUserId,
      assignedAssistantRecord?.UsuarioNombre || assignedAssistant?.UsuarioNombre || assignedUser?.name,
      assistants,
      users,
      assignedAssistantRecord?.Clave || assignedAssistant?.Clave,
    );
    const trackerItem = this.trackerItemForThread(tracker?.items, thread.originDocumentItemId);
    const processStatus = this.processStatusLabel(
      trackerItem?.currentStatus
      || trackerItem?.timeline?.find((stage: any) => ['current', 'blocked'].includes(String(stage?.status || '').toLowerCase()))?.label,
    );

    return {
      customerName: customerName || 'Cliente no disponible',
      sellers,
      assigned,
      processStatus,
    };
  }

  private documentItemForThread(items: any, documentItemId: number | null | undefined): any {
    if (!Array.isArray(items) || !items.length) return null;
    const targetId = Number(documentItemId || 0);
    return items.find((item) => Number(item?.id_item_in_document || 0) === targetId
      || (Array.isArray(item?.line_rows) && item.line_rows.some((row: any) => Number(row?.id || 0) === targetId)))
      || items[0];
  }

  private trackerItemForThread(items: any, documentItemId: number | null | undefined): any {
    if (!Array.isArray(items) || !items.length) return null;
    const targetId = Number(documentItemId || 0);
    return items.find((item) => Number(item?.idItemInDocument || 0) === targetId) || items[0];
  }

  private resolvePerson(
    userId: unknown,
    name: unknown,
    assistants: any[],
    users: any[],
    evOverride?: unknown,
  ): SpecialOrderPersonDisplay | null {
    const normalizedUserId = Number(userId || 0);
    const assistant = assistants.find((candidate) => Number(candidate?.Usuario || 0) === normalizedUserId);
    const user = users.find((candidate) => Number(candidate?.id || 0) === normalizedUserId);
    const displayName = this.firstText(name, assistant?.UsuarioNombre, user?.name);
    const ev = this.firstText(evOverride, assistant?.Clave);
    if (!displayName && !ev) return null;
    return { name: displayName || 'Usuario no identificado', ev: ev || null };
  }

  private personLabel(person: SpecialOrderPersonDisplay): string {
    if (!person.ev) return person.name;
    const evLabel = /^ev\b/i.test(person.ev) ? person.ev : `EV ${person.ev}`;
    return `${person.name} (${evLabel})`;
  }

  private processStatusLabel(status: unknown): string {
    const raw = String(status || '').trim();
    if (!raw) return 'Pendiente';
    const normalized = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const labels: Record<string, string> = {
      requisicion: 'Requisición',
      'po enviada / confirmada': 'PO enviada / confirmada',
      'factura de proveedor': 'Factura de proveedor',
      bol: 'BOL',
      'en warehouse': 'En warehouse',
      'asignado a trailer': 'Asignado a trailer',
      'en recepcion': 'En recepción',
      'cotejar y etiquetar': 'Cotejar y etiquetar',
      'en revision': 'En revisión',
      'centro de armado': 'Centro de armado',
      'en resguardo': 'En resguardo',
    };
    const blockedPrefix = 'bloqueado:';
    if (normalized.startsWith(blockedPrefix)) {
      const blockedStatus = normalized.slice(blockedPrefix.length).trim();
      return `Bloqueado: ${labels[blockedStatus] || raw.slice(blockedPrefix.length).trim()}`;
    }
    return labels[normalized] || raw;
  }

  private firstText(...values: unknown[]): string {
    return values.map((value) => String(value || '').trim()).find(Boolean) || '';
  }

  statusLabel(status: string | null | undefined): string {
    switch (String(status || '').toLowerCase()) {
      case 'completed': return 'Completado';
      case 'unknown': return 'Desconocido';
      case 'incomplete': return 'Incompleto';
      case 'overdue': return 'Atrasado';
      case 'known': return 'Conocida';
      default: return status || 'Pendiente';
    }
  }

  eventTypeLabel(eventType: string | null | undefined): string {
    switch (String(eventType || '').toLowerCase()) {
      case 'initial_definition': return 'Definición inicial';
      case 'cleared': return 'Fecha eliminada';
      case 'changed': return 'Fecha actualizada';
      default: return 'Cambio registrado';
    }
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return 'Sin fecha estimada';
    const raw = String(value).trim();
    const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return dateOnly ? `${dateOnly[2]}/${dateOnly[3]}/${dateOnly[1]}` : raw;
  }

  formatEventDate(value: string | null | undefined): string {
    return formatLocalDateTimeValue(value);
  }

  close(): void {
    this.dialogRef.close();
  }
}
